import { useState, useEffect } from "react";
import { getExchangeRate } from "../services/api";
import type { IExchangeRateResponse } from "../services/api";

/**
 * Country to currency mapping
 */
export const COUNTRY_CURRENCY: Record<string, string> = {
  US: "USD",
  MX: "MXN",
  AR: "ARS",
  CO: "COP",
  BR: "BRL",
  ES: "EUR",
  DE: "EUR",
  FR: "EUR",
};

interface UseExchangeRateResult {
  rate: number | null;
  data: IExchangeRateResponse | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to fetch exchange rate between two currencies
 * @param sourceCurrency - Source currency code (e.g., 'USD')
 * @param destinationCurrency - Destination currency code (e.g., 'MXN')
 */
export const useExchangeRate = (
  sourceCurrency: string,
  destinationCurrency: string
): UseExchangeRateResult => {
  const [data, setData] = useState<IExchangeRateResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchRate = async () => {
    if (!sourceCurrency || !destinationCurrency) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await getExchangeRate(sourceCurrency, destinationCurrency);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to fetch exchange rate"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRate();
  }, [sourceCurrency, destinationCurrency]);

  return {
    rate: data?.rate ?? null,
    data,
    loading,
    error,
    refetch: fetchRate,
  };
};
